import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { Button } from "../components/button";
import { H1 } from "../components/h1";
import { Input } from "../components/input";
import { confirmPasswordReset, requestPasswordReset } from "../services/auth";
import { useLocale } from "../stores/locale";

export function ResetPassword() {
  const { locale, currentLanguage } = useLocale();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const [isValid, setIsValid] = useState(false);

  useEffect(() => {
    if (token) {
      setIsValid(password.length >= 8);
      return;
    }

    setIsValid(email.length > 0);
  }, [email, password, token]);

  if (!token && isSubmitted) {
    return (
      <div className="mx-auto flex w-full max-w-xl flex-col gap-3">
        <H1>{locale.auth.resetRequestSuccessTitle}</H1>
        <p>{locale.auth.resetRequestSuccessBody}</p>
        <Link className="text-blue-700 hover:underline" to="/login">
          {locale.auth.backToLogin}
        </Link>
      </div>
    );
  }

  if (token && isSubmitted) {
    return (
      <div className="mx-auto flex w-full max-w-xl flex-col gap-3">
        <H1>{locale.auth.resetConfirmTitle}</H1>
        <p>{locale.auth.resetConfirmSuccess}</p>
        <Link className="text-blue-700 hover:underline" to="/login">
          {locale.auth.backToLogin}
        </Link>
      </div>
    );
  }

  return (
    <form
      className="mx-auto flex w-full max-w-xl flex-col gap-3"
      onSubmit={(e) => {
        (async () => {
          e.preventDefault();
          setIsLoading(true);
          setHasError(false);
          try {
            if (token) {
              await confirmPasswordReset(token, password, currentLanguage);
            } else {
              await requestPasswordReset(email, currentLanguage);
            }
            setIsSubmitted(true);
          } catch {
            setHasError(true);
          } finally {
            setIsLoading(false);
          }
        })();
      }}
    >
      <H1>
        {token ? locale.auth.resetConfirmTitle : locale.auth.resetRequestTitle}
      </H1>
      {token ? (
        <Input
          disabled={isLoading}
          hasError={hasError}
          placeholder={locale.login.password}
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      ) : (
        <Input
          disabled={isLoading}
          hasError={hasError}
          placeholder={locale.login.email}
          type="text"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      )}
      <div className="mt-2 flex items-center justify-end gap-2">
        {hasError && <p className="text-red-500">{locale.auth.resetError}</p>}
        <Button disabled={!isValid || isLoading} type="submit">
          {token
            ? locale.auth.resetConfirmSubmit
            : locale.auth.resetRequestSubmit}
        </Button>
      </div>
      <Link className="text-sm text-blue-700 hover:underline" to="/login">
        {locale.auth.backToLogin}
      </Link>
    </form>
  );
}
